import { ChangesetConflictDTO, ChangesetMergeDTO, ChangesetResolutionDTO, ResolutionChoice, ResolutionChoices } from "../models/ChangeRequestDTO";

/**
 * The author's answers to the conflicts an update reported, kept by the conflict's identity (page, op, field, rule path or base line:
 * what the server matches a resolution on), and the merged content cut into the pieces the review shows.
 */

export interface Resolution {
    choice: ResolutionChoice;
    /** For "manual": the text that takes the conflict's place. */
    value?: string;
}

export type Resolutions = Record<string, Resolution>;

type ConflictIdentity = Pick<ChangesetConflictDTO, "pageId" | "index" | "field" | "path" | "line">;

/** The identity the server matches a resolution on. */
export function conflictKey(c: ConflictIdentity): string {
    return `${c.pageId}|${c.index}|${c.field}|${c.path ?? ""}|${c.line ?? ""}`;
}

/** A conflict's resolution, sent as the server reads it. */
export function toResolutions(conflicts: ChangesetConflictDTO[], resolutions: Resolutions): ChangesetResolutionDTO[] {
    const out: ChangesetResolutionDTO[] = [];
    for (const c of conflicts) {
        const r = resolutions[conflictKey(c)];
        if (!r) continue;
        out.push({ pageId: c.pageId, index: c.index, field: c.field, path: c.path ?? undefined, line: c.line ?? undefined, choice: r.choice, value: r.choice === ResolutionChoices.Manual ? r.value ?? "" : undefined });
    }
    return out;
}

/** The resolutions that still name one of the conflicts (the others settled conflicts that are gone). */
export function keepMatching(resolutions: Resolutions, conflicts: ChangesetConflictDTO[]): Resolutions {
    const keys = new Set(conflicts.map(conflictKey));
    return Object.fromEntries(Object.entries(resolutions).filter(([key]) => keys.has(key)));
}

export const unresolved = (conflicts: ChangesetConflictDTO[], resolutions: Resolutions) =>
    conflicts.filter((c) => !resolutions[conflictKey(c)]);

/** The text a resolution puts in the conflict's place. */
export function resolvedText(c: ChangesetConflictDTO, r: Resolution): string {
    if (r.choice === ResolutionChoices.Theirs) return c.current ?? "";
    if (r.choice === ResolutionChoices.Manual) return r.value ?? "";
    return c.proposed ?? "";
}

/** A content conflict with a place in the merged content: both sides changed the same lines (a delete or clear has none). */
export const isLineConflict = (c: ChangesetConflictDTO) => c.field === "content" && c.mergedLine != null;

const lines = (text: string | null | undefined) => (text == null || text === "" ? [] : text.split("\n"));

/** A run of merged lines as they are, from `from` (1-based) on. */
export interface TextPiece {
    kind: "text";
    from: number;
    lines: string[];
}

/** A conflicting run: the author's lines (`ours`, what the merged content holds) and the page's (`theirs`). */
export interface ConflictPiece {
    kind: "conflict";
    from: number;
    conflict: ChangesetConflictDTO;
    ours: string[];
    theirs: string[];
}

export type MergedPiece = TextPiece | ConflictPiece;

/**
 * The merged content cut at each line conflict, in order: the text between conflicts and, in each conflict's place, both sides. The
 * merged content carries the author's lines where it conflicts, so a conflict's piece covers `proposed`'s lines from `mergedLine`.
 */
export function splitMerged(merge: ChangesetMergeDTO, conflicts: ChangesetConflictDTO[]): MergedPiece[] {
    const all = lines(merge.content);
    const sorted = conflicts
        .filter((c) => c.pageId === merge.pageId && c.index === merge.index && isLineConflict(c))
        .sort((a, b) => (a.mergedLine as number) - (b.mergedLine as number));
    const pieces: MergedPiece[] = [];
    let at = 0; // 0-based index into `all`
    for (const c of sorted) {
        const start = Math.max(at, Math.min((c.mergedLine as number) - 1, all.length));
        if (start > at) pieces.push({ kind: "text", from: at + 1, lines: all.slice(at, start) });
        const ours = lines(c.proposed);
        pieces.push({ kind: "conflict", from: start + 1, conflict: c, ours, theirs: lines(c.current) });
        at = Math.min(start + ours.length, all.length);
    }
    if (at < all.length) pieces.push({ kind: "text", from: at + 1, lines: all.slice(at) });
    return pieces;
}

/** The merged content with each resolved conflict replaced by what the author chose (unresolved ones keep the author's lines). */
export function applyResolutions(merge: ChangesetMergeDTO, conflicts: ChangesetConflictDTO[], resolutions: Resolutions): string {
    const out: string[] = [];
    for (const piece of splitMerged(merge, conflicts)) {
        if (piece.kind === "text") out.push(...piece.lines);
        else {
            const r = resolutions[conflictKey(piece.conflict)];
            out.push(...(r ? lines(resolvedText(piece.conflict, r)) : piece.ours));
        }
    }
    return out.join("\n");
}
